import { MouseEventHandler } from 'react';

export enum Color {
  PRIMARY = 'PRIMARY',
  PRIMARY_DARK = 'PRIMARY_DARK',
  SECONDARY = 'SECONDARY',
  TERTIARY = 'TERTIARY',
  TRANSPARENT = 'TRANSPARENT',
  GREY = 'GREY',
  ORANGE = 'ORANGE',
  DANGER = 'DANGER',
  BLACK_AND_WHITE = 'BLACK_AND_WHITE',
  PERSIAN_BLUE = 'PERSIAN_BLUE',
  LIGHT_PURPLE = 'LIGHT_PURPLE',
  LIGHT_PURPLE_OPACITY_5 = 'LIGHT_PURPLE_OPACITY_5',
  YELLOW = 'YELLOW',
  GREEN = 'GREEN',
  NAVIGATION_TAB = 'NAVIGATION_TAB',
  NAVIGATION_TAB_ACTIVE = 'NAVIGATION_TAB_ACTIVE',
  PRIMARY_LIGHT = 'PRIMARY_LIGHT',
  PRIMARY_LIGHT_ACTIVE = 'PRIMARY_LIGHT_ACTIVE',
  NAVIGATION_TAB_LIGHT = 'NAVIGATION_TAB_LIGHT',
  NAVIGATION_TAB_LIGHT_ACTIVE = 'NAVIGATION_TAB_LIGHT_ACTIVE',
}

export enum Size {
  EXTRA_SMALL = 'EXTRA_SMALL',
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
  FIT = 'FIT',
  WIDE = 'WIDE',
  FIT_CONTAINER = 'FIT_CONTAINER',
}

export enum TextSize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
}

export enum Align {
  START = 'START',
  CENTER = 'CENTER',
  END = 'END ',
}

export enum TextOverflow {
  HIDDEN = 'HIDDEN',
  VISIBLE = 'VISIBLE',
}

export const ButtonTheme: {
  theme: Record<Color, string>;
  size: Record<Size, string>;
  align: Record<Align, string>;
  textSize: Record<TextSize, string>;
  textOverflow: Record<TextOverflow, string>;
} = {
  theme: {
    [Color.PRIMARY]: 'bg-primary-900 text-primary-50 hover:bg-primary-700 active:bg-primary-600 shadow-sm',
    [Color.PRIMARY_DARK]: 'bg-primary-950 text-primary-50 hover:bg-primary-700 active:bg-primary-600 shadow-sm',
    [Color.SECONDARY]:
      'bg-neutral-50 border border-neutral-300 text-primary-600 hover:bg-neutral-100 active:bg-neutral-200 disabled:border-none shadow-sm',
    [Color.TERTIARY]: 'bg-primary-100 text-primary-600 hover:bg-primary-200 active:bg-primary-300 disabled:border-none',
    [Color.TRANSPARENT]:
      'bg-transparent text-primary-600 hover:bg-primary-50 active:bg-primary-200 disabled:border-none',
    [Color.GREY]: 'bg-neutral-950-opacity-5 text-essential-secondary hover:bg-neutral-950-opacity-10',
    [Color.DANGER]: 'bg-pinkRed-800 text-pinkRed-50 hover:bg-pinkRed-700 active:bg-pinkRed-900',
    [Color.BLACK_AND_WHITE]: 'bg-white border border-neutral-950-opacity-10 hover:bg-neutral-950-opacity-5 active',
    [Color.ORANGE]: 'bg-orange-50 text-orange-700 border border-orange-700',
    [Color.PERSIAN_BLUE]: 'bg-primary-600-opacity-5 hover:bg-primary-600-opacity-10 text-primary-600 shadow-sm',
    [Color.YELLOW]: 'bg-yellow-500 text-neutral-50',
    [Color.GREEN]: 'bg-green-400 text-neutral-50',
    [Color.LIGHT_PURPLE]: 'bg-purple-550 text-purple-50',
    [Color.LIGHT_PURPLE_OPACITY_5]: 'bg-purple-600-opacity-5 text-purple-600',
    [Color.NAVIGATION_TAB]: 'bg-transparent text-neutral-900 rounded-none py-4 px-0 mx-3',
    [Color.NAVIGATION_TAB_ACTIVE]:
      'bg-transparent text-neutral-900 rounded-none py-4 px-0 mx-3 shadow-[inset_0px_-2px_var(--primary-800)]',
    [Color.PRIMARY_LIGHT]: 'bg-neutral-200 text-neutral-950-opacity-30',
    [Color.PRIMARY_LIGHT_ACTIVE]: 'bg-neutral-100 text-primary-900-opacity-40 border border-neutral-300',
    [Color.NAVIGATION_TAB_LIGHT]: 'bg-transparent text-neutral-900 font-medium rounded-none py-4 px-0 mx-2',
    [Color.NAVIGATION_TAB_LIGHT_ACTIVE]:
      'bg-transparent text-neutral-900 font-bold rounded-none py-4 px-0 mx-2 border-b border-primary-800',
  },
  size: {
    [Size.EXTRA_SMALL]: 'p-0.5',
    [Size.SMALL]: 'p-1',
    [Size.MEDIUM]: 'p-2',
    [Size.LARGE]: 'p-3',
    [Size.WIDE]: 'w-full',
    [Size.FIT]: 'w-fit',
    [Size.FIT_CONTAINER]: 'flex-1 p-3',
  },
  align: {
    [Align.START]: 'justify-start',
    [Align.CENTER]: 'justify-center',
    [Align.END]: 'justify-end',
  },
  textSize: {
    [TextSize.SMALL]: 'body-sm',
    [TextSize.MEDIUM]: 'sub-header-base',
  },
  textOverflow: {
    [TextOverflow.HIDDEN]: 'overflow-hidden',
    [TextOverflow.VISIBLE]: 'overflow-visible',
  },
};

export default interface ButtonProps {
  ariaLabel?: string;
  classNames?: string;
  color?: Color;
  dataTestId?: string;
  label?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: () => void;
  onMouseOver?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  size?: Size;
  svg?: string;
  svgClassnames?: string;
  ddActionName?: string;
  textSize?: TextSize;
  textOverflow?: TextOverflow;
  isSpinner?: boolean;
  align?: Align;
  dataPendoId?: string;
}
